import { TimeBlock } from './../../../../time-block/time-block'
import { Observable, combineLatest, forkJoin, of, pipe } from 'rxjs'
import { Component, Input, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/core/core.state'
import { timeBlockSelectors } from 'src/app/features/time-block/time-block.selectors'
import { EmployeeContract } from '../../../employee-contract/employee-contract'
import { EmployeeBooking } from '../../employee-booking'
import { Member } from 'src/app/features/member/member'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import {
  filter,
  map,
  take,
  takeLast,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { timeBlockActions } from 'src/app/features/time-block/time-block.actions'
import { TimeBlockType } from 'src/app/features/time-block/time-block'
import moment from 'moment-mini'

@Component({
  selector: 'invoice-table',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class InvoiceTableComponent implements OnInit {
  @Input() contract: EmployeeContract
  @Input() contractEmployeeBookingsToBeInvoiced: EmployeeBooking[]
  public member$: Observable<Member> = null
  public allTimeBlocksOfMember$: Observable<TimeBlock[]>
  public allTimeBlocksWithOutMember$: Observable<TimeBlock[]>
  public memberId: number
  public memberTimeBlocks = []
  constructor(private store: Store<AppState>) {}

  ngOnInit() {
    this.member$ = this.store.select(authSelectors.selectMember)
    this.allTimeBlocksWithOutMember$ = this.store.select(
      timeBlockSelectors.selectByFilter,
      {
        field: 'memberId',
        value: null
      }
    )
    this.member$.pipe(take(1)).subscribe((member) => {
      this.memberId = member?.id
      this.fetchTimeBlocksOfMember()

      this.allTimeBlocksOfMember$ = this.store.select(
        timeBlockSelectors.selectByFilter,
        {
          field: 'memberId',
          value: member?.id
        }
      )
    })
  }
  public getItemTotal(duration: number, salary: number, vat: number): number {
    return parseFloat((((duration ?? 0) / 60) * (salary ?? 0)).toFixed(2))
  }

  public calculateSalary(
    employeeBooking: EmployeeBooking,
    contract: EmployeeContract
  ): Observable<number> {
    return this.allTimeBlocksOfMember$.pipe(
      map((timeBlocks) => {
        if (employeeBooking.hourlyRate > 0) {
          return employeeBooking.hourlyRate
        }

        const employeeBookingIsoWeekDayNumber = moment(
          employeeBooking.startDate
        ).isoWeekday()
        const targetStartTimeAsNumber = Number(
          moment(employeeBooking.startDate).format('HHmm')
        )

        const targetEndTimeAsNumber =
          Number(moment(employeeBooking.endDate).format('HHmm')) == 0
            ? 2359
            : Number(moment(employeeBooking.endDate).format('HHmm'))

        const overlappingTimeBlock = timeBlocks?.find((timeBlock) => {
          if (timeBlock.isoWeekDayNumber != employeeBookingIsoWeekDayNumber) {
            return false
          }

          const startTimeAsNumber = Number(timeBlock.startTime.replace(':', ''))
          const endTimeAsNumber =
            Number(timeBlock.endTime.replace(':', '')) == 0
              ? 2359
              : Number(timeBlock.endTime.replace(':', ''))

          if (
            endTimeAsNumber > targetStartTimeAsNumber &&
            targetEndTimeAsNumber > startTimeAsNumber
          ) {
            return timeBlock.price > 0
          }

          return false
        })

        if (overlappingTimeBlock?.price > 0) {
          return overlappingTimeBlock.price / 100
        }

        return contract.salary
      })
    )
  }

  private fetchTimeBlocksOfMember() {
    this.store.dispatch(
      timeBlockActions.fetchManyByItemTypeAndId({
        itemId: this.memberId,
        timeBlockType: TimeBlockType.MEMBER
      })
    )
  }

  public calculateHoursMinutes(employeeBooking: EmployeeBooking) {
    const hours = Math.floor(employeeBooking.durationInMinutes / 60)
    const remainingHours = (employeeBooking.durationInMinutes / 60) % 1
    const minutes = Math.floor(remainingHours * 60)

    return {
      hours,
      minutes
    }
  }

  public calculateTotals(
    employeeBookings: any
  ): Observable<{ total: number; subTotal: number }> {
    let total = 0
    let subTotal = 0
    const observables = employeeBookings.map((x) => {
      return this.calculateSalary(x, this.contract).pipe(
        take(1),
        map((salary) => {
          const totalAmount = this.getItemTotal(x.durationInMinutes, salary, 0)
          const subTotalAmount = this.getItemTotal(
            x.durationInMinutes,
            salary,
            this.contract.vat
          )
          return { totalAmount, subTotalAmount }
        })
      )
    })

    return forkJoin(observables).pipe(
      map((results: any) => {
        results.forEach((result: any) => {
          total += result.totalAmount
          subTotal += result.subTotalAmount
        })
        let vat = total * (this.contract.vat / 100)
        let inclVat = total + vat
        return { total, subTotal, vat, inclVat }
      })
    )
  }
}
